import { useState, useCallback, useEffect, useRef } from 'react';

import type { Provider } from '@atlaskit/collab-provider/provider';
import type {
	CollabEditProvider,
	CollabNamespaceLockCheckPayload,
} from '@atlaskit/editor-common/collab';

export const useCollabNamespaceLockCheck = (
	collabEditProvider: (CollabEditProvider & Provider) | null,
	onNamespaceLockCheck?: (isLocked: boolean) => void,
): {
	isLocked: boolean;
} => {
	const [isLocked, setIsLocked] = useState<boolean>(false);
	const namespaceLockCheckRef = useRef<CollabNamespaceLockCheckPayload | null>(null);

	const handleNamespaceLockCheck = useCallback(
		(namespaceLockCheckData: CollabNamespaceLockCheckPayload) => {
			if (collabEditProvider) {
				namespaceLockCheckRef.current = namespaceLockCheckData;
				const namespaceIsLocked = namespaceLockCheckData.isLocked;
				setIsLocked(namespaceIsLocked);
				onNamespaceLockCheck?.(namespaceIsLocked);
			}
		},
		[collabEditProvider, onNamespaceLockCheck],
	);

	useEffect(() => {
		if (!collabEditProvider) {
			return;
		}
		collabEditProvider.on('namespace-lock:check', handleNamespaceLockCheck);
		return () => {
			collabEditProvider.off('namespace-lock:check', handleNamespaceLockCheck);
		};
	}, [collabEditProvider, handleNamespaceLockCheck]);

	return {
		isLocked,
	};
};
